import React from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft } from '../../icons';
import { AppFooter } from './AppFooter';

interface LegalPageLayoutProps {
  /** Route the back link points to. Default: '/' */
  backTo?: string;
  /** Label for the back link. Default: 'Back' */
  backLabel?: string;
  children: React.ReactNode;
}

/**
 * Shell for legal / public pages: back navigation, content area, AppFooter.
 * The content inside is styled via the [&_el]: Tailwind variant selectors on
 * the `.legal-body` wrapper — no custom CSS file needed.
 */
export function LegalPageLayout({
  backTo = '/',
  backLabel = 'Back',
  children,
}: LegalPageLayoutProps) {
  // The legal routes are lazy, so the router's own hash scroll can run before
  // the section exists; scroll once the content has mounted.
  React.useEffect(() => {
    const raw = window.location.hash.slice(1);
    let id = raw;
    try {
      id = decodeURIComponent(raw);
    } catch {
      // Malformed escape (e.g. "#%"): fall back to the raw id.
    }
    if (id) document.getElementById(id)?.scrollIntoView();
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="mx-auto w-full max-w-3xl flex-1 px-6 py-10">
        {/* Back navigation */}
        <Link
          to={backTo}
          className="mb-8 inline-flex items-center gap-1.5 text-body-sm-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft size={14} strokeWidth={2} />
          {backLabel}
        </Link>

        {/* Legal content — child elements styled via descendant selectors */}
        <div
          className={[
            // Paragraphs
            '[&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-muted-foreground',
            // Headings
            '[&_h1]:mb-1 [&_h1]:text-heading-h4 [&_h1]:tracking-tight [&_h1]:text-foreground',
            '[&_h2]:mb-3 [&_h2]:mt-8 [&_h2]:text-heading-h7 [&_h2]:text-foreground',
            '[&_h3]:mb-2 [&_h3]:mt-5 [&_h3]:text-body-md-semibold [&_h3]:text-foreground',
            // Lists
            '[&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-6',
            '[&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6',
            '[&_li]:mb-1 [&_li]:text-muted-foreground',
            // Links
            '[&_a]:text-indigo-600 [&_a:hover]:underline',
            // Sections
            '[&_section]:mt-10',
          ].join(' ')}
        >
          {children}
        </div>
      </div>

      <AppFooter />
    </div>
  );
}
